import { memo, useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faBookmark,
  faMapMarkerAlt,
  faTruck,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import { getCategoryColor } from "../../constants/materialCategories";
import { distanceInKm } from "../../utils/geoUtils";
import { useCarousel } from "../../hooks/useCarousel";
import ActionButton from "../ui/ActionButton";
import "../../style/ListingCard.css";
import "../../style/MaterialCard.css";

const fmtDate = (str) => {
  if (!str) return "—";
  try {
    const [y, m, d] = String(str).split(/[-T]/);
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return String(str).slice(0, 10); }
};

const getDaysLeft = (raw) => {
  if (!raw) return null;
  try {
    const parts = String(raw).split(/[-T]/);
    const dl = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    dl.setHours(0, 0, 0, 0);
    return Math.ceil((dl - now) / 86400000);
  } catch { return null; }
};

// mirrors donor-side status logic
function getListingStatus(listing) {
  const qty = listing.remaining_qty ?? listing.quantity ?? 0;
  const total = listing.quantity ?? 0;
  const pct = total > 0 ? (qty / total) * 100 : 0;
  const isNew = listing.status?.toLowerCase() === "new" && pct === 100;
  if (isNew) return { key: "new", label: "New", cls: "mc__status--new" };
  if (pct === 0) return { key: "donated", label: "Fulfilled", cls: "mc__status--donated" };
  if (pct < 20) return { key: "almost_out", label: "Almost Out", cls: "mc__status--almost" };
  if (pct < 40) return { key: "low", label: "Low Stock", cls: "mc__status--low" };
  return { key: "available", label: "Available", cls: "mc__status--available" };
}

function buildMapLink(rLat, rLon, dLat, dLon) {
  return `https://www.google.com/maps/dir/?api=1&origin=${rLat},${rLon}&destination=${dLat},${dLon}`;
}

const SLIDE_MS = 1800;

function MaterialCard({
  listing, darkMode, onClick, onRequest, onBookmark,
  isBookmarked, isSelected, receiverLat, receiverLon,
}) {
  const { t } = useTranslation();

  const { images: imgs } = useCarousel(listing.images);
  const [idx, setIdx] = useState(0);
  const [sliding, setSliding] = useState(false);
  const [imgHovered, setImgHovered] = useState(false);
  const [showOverlays, setShowOverlays] = useState(false);
  const timerRef = useRef(null);
  const imgCountRef = useRef(imgs.length);
  imgCountRef.current = imgs.length;

  // delay badge + dots so they fade in after the card cascade
  useEffect(() => {
    const id = setTimeout(() => setShowOverlays(true), 900);
    return () => clearTimeout(id);
  }, []);

  const stopSlides = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setSliding(false);
  }, []);

  const startSlides = useCallback(() => {
    if (imgCountRef.current <= 1) return;
    stopSlides();
    setSliding(true);
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % imgCountRef.current);
    }, SLIDE_MS);
  }, [stopSlides]);

  const onImgEnter = useCallback(() => { setImgHovered(true); startSlides(); }, [startSlides]);
  const onImgLeave = useCallback(() => { setImgHovered(false); stopSlides(); setIdx(0); }, [stopSlides]);

  useEffect(() => () => stopSlides(), [stopSlides]);

  const [progressKey, setProgressKey] = useState(0);
  useEffect(() => {
    if (sliding) setProgressKey((k) => k + 1);
  }, [idx, sliding]);

  const title = listing.item_name || listing.material || "—";
  const subtitle = listing.item_name && listing.material ? listing.material : null;
  const isFree = Number(listing.cost) === 0;
  const method = listing.delivery_method || "pickup";
  const isDelivery = method.toLowerCase() === "delivery";
  const qty = listing.remaining_qty ?? listing.quantity ?? 0;
  const status = getListingStatus(listing);

  const hasCoords = receiverLat != null && receiverLon != null && listing.lat != null && listing.lon != null;
  const dist = hasCoords ? distanceInKm([receiverLat, receiverLon], [Number(listing.lat), Number(listing.lon)]) : null;
  const mapHref = hasCoords ? buildMapLink(receiverLat, receiverLon, Number(listing.lat), Number(listing.lon)) : null;

  const daysLeft = getDaysLeft(listing.latest_pickup);
  const isOverdue = daysLeft !== null && daysLeft < 0;

  return (
    <div
      className={`lc mc ${darkMode ? "lc--dark" : "lc--light"} ${isSelected ? "mc--selected" : ""}`}
      role="article" tabIndex={0}
    >
      {/* image — no click, hover shows slideshow + count */}
      <div className="lc__img mc__img-area"
        onMouseEnter={onImgEnter}
        onMouseLeave={onImgLeave}
      >
        {imgs.length > 0 ? (
          <div className="lc__track" style={{ transform: `translateX(-${idx * 100}%)` }}>
            {imgs.map((src, i) => (
              <img key={i} src={src} alt={title} onError={e => { e.target.style.display = "none"; }} />
            ))}
          </div>
        ) : (
          <div className="lc__img-placeholder" />
        )}

        {/* status badge — visible when NOT hovered, delayed entrance */}
        {showOverlays && !imgHovered && (
          <span className={`mc__status ${status.cls}`}>{t(status.label)}</span>
        )}

        {/* image count — visible only on hover */}
        {imgHovered && imgs.length > 1 && (
          <span className="mc__img-count"><FontAwesomeIcon icon={faImage} /> {imgs.length}</span>
        )}

        {showOverlays && imgs.length > 1 && (
          <div className="lc__dots">
            {imgs.map((_, i) => (
              <span key={i} className={`lc__dot ${i === idx ? "lc__dot--active" : ""}`} />
            ))}
          </div>
        )}

        {/* circular timer — only on hover when sliding */}
        {sliding && imgs.length > 1 && (
          <div className="mc__timer">
            <svg className="mc__timer-svg" viewBox="0 0 12 12">
              <circle className="mc__timer-bg" cx="6" cy="6" r="5" />
              <circle key={progressKey} className="mc__timer-fill" cx="6" cy="6" r="5"
                style={{ animationDuration: `${SLIDE_MS}ms` }} />
            </svg>
          </div>
        )}
      </div>

      {/* body — redesigned layout */}
      <div className="lc__body mc__body" style={{ padding: "10px 14px 10px" }}>
        <div className="mc__inner">
          {/* price + title row */}
          <div className="mc__head">
            <div className="mc__head-left">
              <h3 className="lc__title">{title}</h3>
              {subtitle && <span className="mc__subtitle">{subtitle}</span>}
            </div>
            <div className="mc__head-price">
              {isFree ? (
                <span className="mc__price-big mc__price-big--free">{t("Free")}</span>
              ) : (
                <>
                  <span className="mc__price-big">${Number(listing.cost || 0).toFixed(2)}</span>
                  <span className="mc__price-unit">{t("per lb")}</span>
                </>
              )}
            </div>
          </div>

          {/* badges row */}
          <div className="mc__badges">
            {listing.material_category && (
              <span className="mc__badge" style={{ backgroundColor: getCategoryColor(listing.material_category) }}>
                {t(listing.material_category)}
              </span>
            )}
            <span className="mc__badge mc__badge--available">{qty} lbs</span>
            <span className={`mc__badge mc__badge--method ${isDelivery ? "mc__badge--delivery" : ""}`}>
              {isDelivery && <FontAwesomeIcon icon={faTruck} className="mc__badge-icon" />}
              {t(isDelivery ? "Delivery" : "Pickup")}
            </span>
          </div>

          {/* date row */}
          <div className="mc__date-row">
            <FontAwesomeIcon icon={faClock} className="mc__date-clock" />
            <span className={`mc__date-val ${isOverdue ? "lc__deadline-val--overdue" : ""}`}>
              {fmtDate(listing.latest_pickup)}
              {isOverdue && (
                <span className="lc__urgency lc__urgency--overdue">{t("Overdue")}</span>
              )}
            </span>
          </div>
        </div>

        {/* hover-reveal: company + distance — outside mc__inner so no phantom gap */}
        <div className="mc__reveal mc__reveal-row">
          {listing.companyName && (
            <span className="mc__reveal-company">{listing.companyName}</span>
          )}
          {dist !== null && mapHref && (
            <a className="mc__reveal-dist" href={mapHref}
              target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon icon={faMapMarkerAlt} /> {Math.round(dist)} km
            </a>
          )}
        </div>

        {/* hover-reveal: cta */}
        <div className="mc__reveal mc__cta-row">
          <ActionButton variant={isSelected ? "primary" : "secondary"} className="mc__cta"
            onClick={(e) => { e.stopPropagation(); onRequest?.(listing); }}>
            {isSelected ? t("Selected") : t("Request Material")}
          </ActionButton>
          <button
            className={`mc__bookmark-btn ${isBookmarked ? "mc__bookmark-btn--active" : ""}`}
            onClick={(e) => { e.stopPropagation(); onBookmark?.(listing.id); }}
            aria-label={isBookmarked ? "Remove bookmark" : "Bookmark listing"}>
            <FontAwesomeIcon icon={faBookmark} />
          </button>
        </div>
      </div>
    </div>
  );
}

MaterialCard.propTypes = {
  listing: PropTypes.object.isRequired,
  darkMode: PropTypes.bool,
  onClick: PropTypes.func,
  onRequest: PropTypes.func,
  onBookmark: PropTypes.func,
  isBookmarked: PropTypes.bool,
  isSelected: PropTypes.bool,
  receiverLat: PropTypes.number,
  receiverLon: PropTypes.number,
};

export default memo(MaterialCard);
